import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import { formatJoinedDate } from '../api/agents';
import {
  SIGN_IN_LABEL,
  UserDetail as UserDetailData,
  UserListingSummary,
  getUserDetail,
} from '../api/users';
import {
  formatAmount,
  statusBadgeClass as orderBadge,
  statusLabel as orderLabel,
} from '../api/orders';
import {
  statusBadgeClass as appBadge,
  statusLabel as appLabel,
} from '../api/applications';
import { extractApiError } from '../api/legalServices';

/** Strip the trailing /api so relatively-stored photo paths still resolve. */
const API_ORIGIN = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api'
).replace(/\/api\/?$/, '');

const resolvePhoto = (u: string): string =>
  /^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`;

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatListingPrice = (l: UserListingSummary): string => {
  const n = Number(l.price);
  if (!Number.isFinite(n)) return '—';
  return '$' + n.toLocaleString() + (l.type === 'Rental' ? '/mo' : '');
};

const money = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const cap = (v?: string | null): string => (v ? v.charAt(0).toUpperCase() + v.slice(1) : '—');

// ─── Small pieces ──────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-center gap-3 py-2.5 border-b border-g100 last:border-b-0">
    <span className="text-[13px] text-g500 flex-shrink-0">{label}</span>
    <span className="text-[13px] text-ink font-semibold text-right min-w-0 truncate">{children}</span>
  </div>
);

interface StatProps {
  icon: string;
  label: string;
  value: React.ReactNode;
  color: string;
  bg: string;
}

const Stat: React.FC<StatProps> = ({ icon, label, value, color, bg }) => (
  <div className="card flex-1 min-w-[150px] p-4">
    <div className="flex items-center gap-2.5 mb-2">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: bg }}>
        <Icon name={icon} size={18} color={color} />
      </div>
      <div className="text-[12.5px] text-g500 font-medium">{label}</div>
    </div>
    <div className="text-[22px] font-extrabold text-ink leading-tight">{value}</div>
  </div>
);

const Th: React.FC<{ children?: React.ReactNode; right?: boolean }> = ({ children, right }) => (
  <th
    className={
      'px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap' +
      (right ? ' text-right' : '')
    }
  >
    {children}
  </th>
);

const Td: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className }) => (
  <td className={'px-4 py-3 text-sm border-b border-g100 ' + (className ?? 'whitespace-nowrap')}>{children}</td>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div className="py-12 text-center text-sm text-g500">{text}</div>
);

const TableCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="bg-white rounded-card overflow-x-auto border border-g100"
    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}
  >
    {children}
  </div>
);

const ListingCell: React.FC<{ listing: UserListingSummary | null; onOpen: (id: number) => void }> = ({
  listing,
  onOpen,
}) => {
  if (!listing) return <span className="text-g400 italic">Listing removed</span>;
  const thumb = listing.coverPhoto || (Array.isArray(listing.photos) && listing.photos[0]) || null;
  return (
    <button
      type="button"
      className="flex items-center gap-3 text-left min-w-[220px] group"
      onClick={() => onOpen(listing.id)}
    >
      <div
        className="w-12 h-9 rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center"
        style={{ background: 'var(--g100)' }}
      >
        {thumb ? (
          <img src={resolvePhoto(thumb)} alt="" className="w-full h-full object-cover" />
        ) : (
          <Icon name="building-2" size={16} color="var(--g400)" />
        )}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-ink truncate group-hover:text-brand">{listing.title}</div>
        <div className="text-xs text-g500 truncate">{listing.address}</div>
      </div>
    </button>
  );
};

// ─── Page ──────────────────────────────────────────────────────────

type Tab = 'applications' | 'legal' | 'requests' | 'inquiries' | 'liked' | 'saved' | 'reviews';

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('applications');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getUserDetail(Number(id)));
    } catch (err) {
      setError(extractApiError(err, 'Failed to load user'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openListing = (listingId: number) => navigate(`/admin/listings/${listingId}`);

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="user" size={28} color="var(--g400)" /> Loading user...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card p-6 max-w-[520px]">
        <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--red)' }}>
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load this user</strong>
        </div>
        <div className="text-sm text-g600 mb-4">{error || 'User not found.'}</div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/users')}>
            Back to users
          </button>
        </div>
      </div>
    );
  }

  const { user, stats } = data;

  const TABS: { id: Tab; label: string; icon: string; count: number }[] = [
    { id: 'applications', label: 'Applications', icon: 'file-text', count: stats.applications },
    { id: 'legal', label: 'Legal Services Request', icon: 'scale', count: stats.legalOrders },
    { id: 'requests', label: 'Service Order', icon: 'wrench', count: stats.maintenanceRequests },
    { id: 'inquiries', label: 'Inquiries', icon: 'message-square', count: stats.inquiries },
    { id: 'liked', label: 'Liked', icon: 'heart', count: stats.likedListings },
    { id: 'saved', label: 'Saved', icon: 'star', count: stats.savedListings },
    { id: 'reviews', label: 'Reviews', icon: 'star', count: stats.reviews },
  ];

  const savedListingRows = (
    rows: { listing: UserListingSummary | null; at: string }[],
    dateLabel: string,
    emptyText: string
  ) =>
    rows.length === 0 ? (
      <Empty text={emptyText} />
    ) : (
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <Th>Property</Th>
            <Th>Type</Th>
            <Th>Price</Th>
            <Th>Status</Th>
            <Th>{dateLabel}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-g50">
              <Td className="">
                <ListingCell listing={r.listing} onOpen={openListing} />
              </Td>
              <Td>
                {r.listing && (
                  <span className={`type-badge type-${r.listing.type.toLowerCase()}`}>
                    {r.listing.type === 'Sale' ? 'Buy' : r.listing.type}
                  </span>
                )}
              </Td>
              <Td className="whitespace-nowrap font-semibold">{r.listing ? formatListingPrice(r.listing) : '—'}</Td>
              <Td>
                {r.listing && (
                  <span className={'badge ' + (r.listing.status === 'active' ? 'badge-active' : 'badge-pending')}>
                    {r.listing.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                )}
              </Td>
              <Td className="whitespace-nowrap text-g500">{formatJoinedDate(r.at)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    );

  let body: React.ReactNode;
  switch (tab) {
    case 'applications':
      body =
        data.applications.length === 0 ? (
          <Empty text="No rental applications yet." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Property</Th>
                <Th>Applicant name</Th>
                <Th>Fee</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
              </tr>
            </thead>
            <tbody>
              {data.applications.map((a) => (
                <tr key={a.id} className="hover:bg-g50">
                  <Td className="whitespace-nowrap font-semibold">{a.referenceNo}</Td>
                  <Td className="">
                    <ListingCell listing={a.listing} onOpen={openListing} />
                  </Td>
                  <Td>
                    {a.fullName}
                    {a.phone && <div className="text-xs text-g500">{a.phone}</div>}
                  </Td>
                  <Td>
                    {formatAmount(a.applicationFee)}{' '}
                    <span className="text-xs text-g500">{a.feePaid ? '· Paid' : '· Unpaid'}</span>
                  </Td>
                  <Td>
                    <span className={appBadge(a.status)}>{appLabel(a.status)}</span>
                  </Td>
                  <Td className="whitespace-nowrap text-g500">{formatDateTime(a.createdAt)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      break;
    case 'legal': {
      const legal = data.orders.filter((o) => o.kind === 'legal');
      body =
        legal.length === 0 ? (
          <Empty text="No legal services requests yet." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Service</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Placed</Th>
              </tr>
            </thead>
            <tbody>
              {legal.map((o) => (
                <tr
                  key={o.id}
                  className="hover:bg-g50 cursor-pointer"
                  onClick={() => navigate(`/admin/orders/${o.id}`)}
                >
                  <Td className="whitespace-nowrap font-semibold">{o.orderRef}</Td>
                  <Td className="min-w-[180px]">
                    {o.serviceName || '—'}
                    {o.tier && <div className="text-xs text-g500">{o.tier}</div>}
                  </Td>
                  <Td>
                    {formatAmount(o.amount)}{' '}
                    {o.amount !== null && <span className="text-xs text-g500">{o.paid ? '· Paid' : '· Unpaid'}</span>}
                  </Td>
                  <Td>
                    <span className={orderBadge(o.status)}>{orderLabel(o.status)}</span>
                  </Td>
                  <Td className="whitespace-nowrap text-g500">{formatDateTime(o.createdAt)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      break;
    }
    case 'requests': {
      const requests = data.orders.filter((o) => o.kind === 'maintenance');
      body =
        requests.length === 0 ? (
          <Empty text="No service orders yet." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Service</Th>
                <Th>Urgency</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
              </tr>
            </thead>
            <tbody>
              {requests.map((o) => (
                <tr
                  key={o.id}
                  className="hover:bg-g50 cursor-pointer"
                  onClick={() => navigate(`/admin/service-requests/${o.id}`)}
                >
                  <Td className="whitespace-nowrap font-semibold">{o.orderRef}</Td>
                  <Td className="min-w-[180px]">{o.serviceName || o.category || '—'}</Td>
                  <Td>
                    {o.urgency === 'urgent' ? (
                      <span className="text-xs font-semibold" style={{ color: 'var(--red)' }}>Urgent</span>
                    ) : (
                      cap(o.urgency)
                    )}
                  </Td>
                  <Td>
                    <span className={orderBadge(o.status)}>{orderLabel(o.status)}</span>
                  </Td>
                  <Td className="whitespace-nowrap text-g500">{formatDateTime(o.createdAt)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      break;
    }
    case 'inquiries':
      body =
        data.inquiries.length === 0 ? (
          <Empty text="No inquiries sent yet." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <Th>Property</Th>
                <Th>Message</Th>
                <Th>Status</Th>
                <Th>Sent</Th>
              </tr>
            </thead>
            <tbody>
              {data.inquiries.map((q) => (
                <tr key={q.id} className="hover:bg-g50">
                  <Td className="">
                    <ListingCell listing={q.listing} onOpen={openListing} />
                  </Td>
                  <Td className="text-g700 min-w-[240px] max-w-[420px]">
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{q.message || '—'}</div>
                    {q.phone && <div className="text-xs text-g500 mt-1">Phone: {q.phone}</div>}
                  </Td>
                  <Td>
                    <span className={`badge badge-${q.status}`}>{cap(q.status)}</span>
                  </Td>
                  <Td className="whitespace-nowrap text-g500">{formatDateTime(q.createdAt)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      break;
    case 'liked':
      body = savedListingRows(
        data.likedListings.map((l) => ({ listing: l.listing, at: l.likedAt })),
        'Liked on',
        'No liked properties yet.'
      );
      break;
    case 'saved':
      body = savedListingRows(
        data.savedListings.map((l) => ({ listing: l.listing, at: l.savedAt })),
        'Saved on',
        'No saved properties yet.'
      );
      break;
    case 'reviews':
      body =
        data.reviews.length === 0 ? (
          <Empty text="No reviews written yet." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <Th>Service</Th>
                <Th>Rating</Th>
                <Th>Comment</Th>
                <Th>Date</Th>
              </tr>
            </thead>
            <tbody>
              {data.reviews.map((r) => (
                <tr key={r.id} className="hover:bg-g50">
                  <Td>
                    {r.serviceName || '—'}
                    {r.providerName && <div className="text-xs text-g500">{r.providerName}</div>}
                  </Td>
                  <Td>
                    <span className="font-semibold" style={{ color: 'var(--yellow)' }}>
                      {'★'.repeat(Math.round(r.rating))}
                      <span style={{ color: 'var(--g200)' }}>{'★'.repeat(Math.max(0, 5 - Math.round(r.rating)))}</span>
                    </span>
                  </Td>
                  <Td className="text-g700 min-w-[240px] max-w-[420px]">
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.comment || '—'}</div>
                    {Array.isArray(r.tags) && r.tags.length > 0 && (
                      <div className="text-xs text-g500 mt-1">{r.tags.join(' · ')}</div>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap text-g500">{formatJoinedDate(r.createdAt)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      break;
  }

  return (
    <div className="animate-fadeIn">
      <div className="mb-6">
        <button
          className="flex items-center gap-2 text-sm font-semibold text-g600 hover:text-ink transition-colors"
          onClick={() => navigate('/admin/users')}
        >
          <Icon name="arrow-left" size={18} /> Back to users
        </button>
      </div>

      {/* Profile + account */}
      <div className="grid gap-5 lg:grid-cols-2 items-start mb-5">
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <Avatar src={user.avatar} name={user.name || user.email} id={user.id} size={64} radius={16} />
            <div className="min-w-0 flex-1">
              <div className="text-lg font-bold text-ink truncate">
                {user.name || <span className="text-g400 italic">Unnamed</span>}
              </div>
              <div className="text-[13px] text-g500 flex items-center gap-1.5 min-w-0">
                <Icon name="mail" size={13} color="var(--g400)" />
                <a href={`mailto:${user.email}`} className="truncate hover:text-brand">
                  {user.email}
                </a>
              </div>
            </div>
            <span className="badge badge-new flex-shrink-0">App user</span>
          </div>
          <div className="mt-5 pt-4 border-t border-g100 grid grid-cols-2 gap-4">
            <div>
              <div className="text-[12px] text-g500">Total spent</div>
              <div className="text-[22px] font-extrabold text-brand leading-tight">{money(stats.totalSpent)}</div>
              <div className="text-[11.5px] text-g400 mt-0.5">Paid orders + application fees</div>
            </div>
            <div>
              <div className="text-[12px] text-g500">Member since</div>
              <div className="text-[22px] font-extrabold text-ink leading-tight">{formatJoinedDate(user.createdAt)}</div>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-2.5 mb-3">
            <Icon name="user" size={20} color="var(--brand)" />
            <span className="text-base font-bold">Account</span>
          </div>
          <InfoRow label="User ID">#{user.id}</InfoRow>
          <InfoRow label="Sign-in method">
            {user.provider ? SIGN_IN_LABEL(user.provider) : 'Email & password'}
          </InfoRow>
          <InfoRow label="Saved payment profile">{user.stripeCustomerId ? 'Yes (Stripe)' : 'No'}</InfoRow>
          <InfoRow label="Joined">{formatDateTime(user.createdAt)}</InfoRow>
          <InfoRow label="Last updated">{formatDateTime(user.updatedAt)}</InfoRow>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-4 flex-wrap mb-6">
        <Stat icon="file-text" label="Applications" value={stats.applications} color="var(--sky)" bg="var(--sky-bg)" />
        <Stat icon="scale" label="Legal services requests" value={stats.legalOrders} color="var(--brand)" bg="var(--brand-bg)" />
        <Stat icon="wrench" label="Service orders" value={stats.maintenanceRequests} color="var(--yellow)" bg="var(--yellow-bg)" />
        <Stat icon="message-square" label="Inquiries" value={stats.inquiries} color="var(--blue)" bg="var(--blue-bg)" />
        <Stat icon="heart" label="Liked" value={stats.likedListings} color="var(--red)" bg="var(--red-bg)" />
        <Stat icon="star" label="Reviews" value={stats.reviews} color="var(--green)" bg="var(--green-bg)" />
      </div>

      {/* Activity tabs */}
      <div className="flex mb-4 overflow-x-auto" style={{ borderBottom: '2px solid var(--g100)' }} role="tablist">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-all"
              style={{
                color: active ? 'var(--brand)' : 'var(--g500)',
                marginBottom: -2,
                background: 'none',
                border: 'none',
                borderBottomWidth: 2,
                borderBottomStyle: 'solid',
                borderBottomColor: active ? 'var(--brand)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <Icon name={t.icon} size={15} color={active ? 'var(--brand)' : 'var(--g500)'} />
              {t.label}
              <span
                className="text-[11px] font-bold rounded-full px-1.5 min-w-[20px] text-center"
                style={{
                  background: active ? 'var(--brand-bg)' : 'var(--g100)',
                  color: active ? 'var(--brand)' : 'var(--g500)',
                }}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>
      <TableCard>{body}</TableCard>
    </div>
  );
};

export default UserDetail;
